import React, { useState } from 'react';
import { FaTimes, FaFileExcel, FaFilePdf, FaFileAlt, FaDownload, FaCalendarAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

function ExportModal({ isOpen, onClose, onExport, title = 'Export Report', data = [], headers = [], filename = 'report' }) {
  const [exportFormat, setExportFormat] = useState('csv');
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  const [includeHeaders, setIncludeHeaders] = useState(true);
  const [exporting, setExporting] = useState(false);

  if (!isOpen) return null;

  const handleExport = async () => {
    if (data.length === 0) {
      toast.warning('No data to export');
      return;
    }

    setExporting(true);
    try {
      // Filter data by date range if date fields exist
      let exportData = data;
      if (dateRange.start && dateRange.end) {
        const hasDateField = data.some(row => row.date || row.created_at || row.fuel_date || row.payment_date);
        if (hasDateField) {
          exportData = data.filter(row => {
            const rowDate = row.date || row.created_at || row.fuel_date || row.payment_date;
            if (!rowDate) return true;
            return rowDate >= dateRange.start && rowDate <= dateRange.end;
          });
        }
      }

      if (exportData.length === 0) {
        toast.warning('No data in selected date range');
        setExporting(false);
        return;
      }

      const actualHeaders = includeHeaders ? headers : Object.keys(exportData[0] || {});
      const filenameWithDate = `${filename}_${dateRange.start}_to_${dateRange.end}`;

      if (exportFormat === 'csv') {
        // CSV Export
        let csvContent = '';
        if (includeHeaders && actualHeaders.length > 0) {
          csvContent = actualHeaders.join(',') + '\n';
        }
        
        exportData.forEach(row => {
          const values = actualHeaders.map(header => {
            const key = header.toLowerCase().replace(/ /g, '_').replace(/[()%#]/g, '').replace(/\//g, '_');
            let value = row[key] || row[header] || '';
            if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
              value = `"${value.replace(/"/g, '""')}"`;
            }
            return value;
          });
          csvContent += values.join(',') + '\n';
        });

        const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `${filenameWithDate}.csv`;
        link.click();
        URL.revokeObjectURL(link.href);
        toast.success(`✅ Report exported as CSV!`);
        
      } else if (exportFormat === 'json') {
        // JSON Export
        const jsonContent = JSON.stringify(exportData, null, 2);
        const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `${filenameWithDate}.json`;
        link.click();
        URL.revokeObjectURL(link.href);
        toast.success(`✅ Report exported as JSON!`);
        
      } else if (exportFormat === 'pdf') {
        // ============================================
        // PDF EXPORT - USING jsPDF
        // ============================================
        const doc = new jsPDF('landscape', 'mm', 'a4');
        const pageWidth = doc.internal.pageSize.getWidth();
        
        // Title
        const reportTitle = title || 'Report';
        doc.setFontSize(16);
        doc.setTextColor(10, 25, 41); // Navy color
        doc.text(reportTitle, pageWidth / 2, 20, { align: 'center' });
        
        // Date range
        doc.setFontSize(10);
        doc.setTextColor(100, 100, 100);
        doc.text(`Date Range: ${dateRange.start} to ${dateRange.end}`, pageWidth / 2, 28, { align: 'center' });
        doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, 34, { align: 'center' });

        // Table
        const tableHeaders = actualHeaders.map(h => h);
        const tableRows = exportData.map(row => {
          return actualHeaders.map(header => {
            const key = header.toLowerCase().replace(/ /g, '_').replace(/[()%#]/g, '').replace(/\//g, '_');
            let value = row[key] || row[header] || '';
            if (typeof value === 'number') {
              return value.toLocaleString();
            }
            return value;
          });
        });

        autoTable(doc, {
          head: [tableHeaders],
          body: tableRows,
          startY: 42,
          theme: 'striped',
          headStyles: {
            fillColor: [10, 25, 41],
            textColor: [255, 255, 255],
            fontStyle: 'bold',
            fontSize: 10
          },
          bodyStyles: {
            fontSize: 9,
            cellPadding: { top: 3, bottom: 3, left: 3, right: 3 }
          },
          alternateRowStyles: {
            fillColor: [245, 247, 250]
          },
          margin: { left: 10, right: 10 },
          styles: {
            overflow: 'linebreak'
          }
        });

        // Footer
        const finalY = doc.lastAutoTable?.finalY || 200;
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(`One Purpose Logistics - Export Report`, pageWidth / 2, finalY + 10, { align: 'center' });
        doc.text(`Page 1 of 1`, pageWidth / 2, finalY + 16, { align: 'center' });

        // Save PDF
        doc.save(`${filenameWithDate}.pdf`);
        toast.success(`✅ Report exported as PDF!`);
      }

      onExport && onExport(exportFormat, exportData);
      onClose();
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export report: ' + error.message);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold" style={{ color: '#0A1929' }}>
            <FaDownload className="inline mr-2" style={{ color: '#00A86B' }} />
            {title}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-navy">
            <FaTimes size={20} />
          </button>
        </div>

        <p className="text-sm text-gray-500 mb-4">
          {data.length} records available for export
        </p>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <FaCalendarAlt className="inline mr-2" /> Date Range
          </label>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Start</label>
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:border-green"
                style={{ borderColor: '#E8ECEF' }}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">End</label>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:border-green"
                style={{ borderColor: '#E8ECEF' }}
              />
            </div>
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Export Format</label>
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={() => setExportFormat('csv')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                exportFormat === 'csv' 
                  ? 'bg-green-500 text-white' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <FaFileExcel size={16} /> CSV
            </button>
            <button
              onClick={() => setExportFormat('json')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                exportFormat === 'json' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <FaFileAlt size={16} /> JSON
            </button>
            <button
              onClick={() => setExportFormat('pdf')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                exportFormat === 'pdf' 
                  ? 'bg-red-500 text-white' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <FaFilePdf size={16} /> PDF
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-4">
          <input
            type="checkbox"
            id="includeHeaders"
            checked={includeHeaders}
            onChange={(e) => setIncludeHeaders(e.target.checked)}
            className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
          />
          <label htmlFor="includeHeaders" className="text-sm text-gray-600">
            Include column headers
          </label>
        </div>

        <div className="bg-gray-50 rounded-lg p-3 mb-4 text-sm" style={{ color: '#4A5568' }}>
          <div className="flex justify-between">
            <span>Total Records:</span>
            <span className="font-medium">{data.length}</span>
          </div>
          <div className="flex justify-between">
            <span>Format:</span>
            <span className="font-medium uppercase">{exportFormat}</span>
          </div>
          <div className="flex justify-between">
            <span>Date Range:</span>
            <span className="font-medium">{dateRange.start} → {dateRange.end}</span>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border rounded-lg text-sm hover:bg-gray-50"
            style={{ borderColor: '#E8ECEF' }}
          >
            Cancel
          </button>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="px-4 py-2 rounded-lg text-white text-sm font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
            style={{ backgroundColor: '#00A86B' }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#00995E'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#00A86B'}
          >
            <FaDownload size={14} />
            {exporting ? 'Exporting...' : `Export ${exportFormat.toUpperCase()}`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ExportModal;
